import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareAlt, faLink, faCheck, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import {
  faLinkedinIn,
  faWhatsapp,
  faTwitter,
  faFacebookF,
} from '@fortawesome/free-brands-svg-icons';
import { fetchVanityPath } from '../../../util/vanityUrl';
import css from './ShareButton.module.css';

// Canonical page URL without query/hash (drops tracking params, booking
// state etc. so everyone shares the same clean link).
const currentPageUrl = () =>
  typeof window === 'undefined' ? '' : `${window.location.origin}${window.location.pathname}`;

export const shareTargets = (url, title) => {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title || '');
  return [
    {
      key: 'linkedin',
      label: 'LinkedIn',
      icon: faLinkedinIn,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: faWhatsapp,
      href: `https://wa.me/?text=${encodeURIComponent(`${title ? `${title} ` : ''}${url}`)}`,
    },
    {
      key: 'x',
      label: 'X (Twitter)',
      icon: faTwitter,
      href: `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
    },
    {
      key: 'facebook',
      label: 'Facebook',
      icon: faFacebookF,
      href: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    },
    {
      key: 'email',
      label: 'Email',
      icon: faEnvelope,
      href: `mailto:?subject=${t}&body=${u}`,
    },
  ];
};

const copyText = text => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  // Fallback for older browsers / non-https.
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  try {
    document.execCommand('copy');
    return Promise.resolve();
  } catch (e) {
    return Promise.reject(e);
  } finally {
    document.body.removeChild(el);
  }
};

const LISTING_PATH_RE = /^\/l\/[^/]+\/[0-9a-f-]{36}\/?$/i;

// Swap /l/<slug>/<uuid> in the address bar for the personalized URL, so a
// link copied straight from the browser is the pretty one too. Done via the
// History API directly: the page is already rendered, and remounting it
// through React Router would refetch everything for no visible change.
const showPrettyPathInAddressBar = prettyPath => {
  const { pathname, search, hash } = window.location;
  if (!LISTING_PATH_RE.test(pathname)) return;
  window.history.replaceState(window.history.state, '', `${prettyPath}${search}${hash}`);
};

const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(pointer: coarse)').matches;

export default function ShareButton({ title, text, className, listingId, syncAddressBar }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [prettyPath, setPrettyPath] = useState(null);
  const rootRef = useRef(null);

  // Personalized listing URL (/<coach>/<listing>). Fetched up front so the
  // native share sheet can open synchronously on click.
  useEffect(() => {
    if (!listingId) return undefined;
    let cancelled = false;
    fetchVanityPath(listingId).then(path => {
      if (cancelled || !path) return;
      setPrettyPath(path);
      if (syncAddressBar) showPrettyPathInAddressBar(path);
    });
    return () => {
      cancelled = true;
    };
  }, [listingId, syncAddressBar]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const url =
    prettyPath && typeof window !== 'undefined'
      ? `${window.location.origin}${prettyPath}`
      : currentPageUrl();

  const handleClick = () => {
    // Phones/tablets: use the native share sheet when available.
    if (isTouchDevice() && navigator.share) {
      navigator.share({ title, text, url }).catch(() => {
        // User cancelled or share failed — fall back to our menu.
        setOpen(true);
      });
      return;
    }
    setOpen(o => !o);
  };

  const handleCopy = () => {
    copyText(url)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  return (
    <div className={[css.root, className].filter(Boolean).join(' ')} ref={rootRef}>
      <button
        type="button"
        className={css.button}
        onClick={handleClick}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <FontAwesomeIcon icon={faShareAlt} className={css.buttonIcon} />
        Share
      </button>

      {open && (
        <div className={css.menu} role="menu">
          <button type="button" role="menuitem" className={css.menuItem} onClick={handleCopy}>
            <FontAwesomeIcon icon={copied ? faCheck : faLink} className={css.menuIcon} />
            {copied ? 'Link copied!' : 'Copy link'}
          </button>
          {shareTargets(url, title).map(target => (
            <a
              key={target.key}
              role="menuitem"
              className={css.menuItem}
              href={target.href}
              target={target.key === 'email' ? undefined : '_blank'}
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
            >
              <FontAwesomeIcon icon={target.icon} className={css.menuIcon} />
              {target.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
